import { useGlobalDiscoveryFacets } from "@entities/global-discovery/hooks/useGlobalDiscoveryFacets";
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { ConfigGlobalTypeSingle } from "@shared/lib/types";
import { SelectButton } from "@shared/ui/Select";
import { useEffect, useMemo, useState } from "react";
import styles from "./ListFilter.module.scss";

interface ListFilterProps {
  filter: ConfigGlobalTypeSingle;
  showTitle?: boolean;
  selectedFilters?: any;
  setSelectedFilter?: (updateFn: (value: any) => any) => void;
  platformId?: string;
  options?: { label: string; value: string }[];
  loading?: boolean;
}

export function ListFilter({
  filter,
  showTitle = true,
  selectedFilters: selectedFiltersProp,
  setSelectedFilter: setSelectedFilterProp,
  platformId: platformIdProp,
  options: optionsProp,
  loading: loadingProp,
}: ListFilterProps) {
  const globalStore = useGetGlobalDiscoveryStore();
  const selectedFilters = selectedFiltersProp ?? globalStore.selectedFilters;
  const setSelectedFilter = setSelectedFilterProp ?? globalStore.setSelectedFilter;
  const platformId = platformIdProp ?? globalStore.platformId;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch facet options from backend
  const { facet, isFetching: isFacetsFetching } = useGlobalDiscoveryFacets({
    facetName: filter.facetName,
    body: {},
    query: debouncedQuery,
    work_platform_id: platformId,
    enabled: !optionsProp && !!platformId,
  });

  const isFetching = optionsProp ? !!loadingProp : isFacetsFetching;

  // Convert facet data to Select options, including selected values not in facets
  const options = useMemo(() => {
    if (optionsProp) {
      if (searchQuery) {
        return optionsProp.filter((opt) =>
          opt.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      return optionsProp;
    }

    const facetOptions = (facet || []).map((item) => ({
      label: item.name,
      value: item.value,
    }));

    const facetValues = new Set(facetOptions.map((opt) => opt.value));
    const selectedValue = selectedFilters[filter.facetName];
    const filterTags = selectedFilters.filterTags || {};

    // If selected value exists and is not in facet results, add it from filterTags
    if (selectedValue && !facetValues.has(selectedValue)) {
      const labelFromFilterTags = filterTags[filter.facetName];
      if (labelFromFilterTags) {
        return [
          ...facetOptions,
          {
            label:
              typeof labelFromFilterTags === "string"
                ? labelFromFilterTags
                : selectedValue,
            value: selectedValue,
          },
        ];
      }
    }

    return facetOptions;
  }, [facet, selectedFilters, filter.facetName, optionsProp, searchQuery]);

  // Get current selected value (SelectButton uses simple value, not labelInValue)
  const currentValue = useMemo(() => {
    const value = selectedFilters[filter.facetName];
    return value ? String(value) : undefined;
  }, [selectedFilters, filter.facetName]);

  const handleChange = (value: string | string[] | undefined, option: any) => {
    // SelectButton onChange receives value directly (not labelInValue format)
    if (!value) {
      setSelectedFilter((prevFilters) => {
        return {
          ...prevFilters,
          [filter.facetName]: undefined,
          filterTags: {
            ...(prevFilters.filterTags || {}),
            [filter.facetName]: undefined,
          },
        };
      });
      return;
    }

    // Handle single select (not array)
    const selectedValue = Array.isArray(value) ? value[0] : value;
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const label = selectedOption?.label || selectedValue;

    setSelectedFilter((prevFilters) => {
      return {
        ...prevFilters,
        [filter.facetName]: selectedValue,
        filterTags: {
          ...(prevFilters.filterTags || {}),
          [filter.facetName]: selectedValue ? label : undefined,
        },
      };
    });
  };

  const handleSelect = () => {
    // Clear search query immediately when an option is selected
    setSearchQuery("");
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    // Clear search query when dropdown closes
    if (!open) {
      setSearchQuery("");
    }
  };

  return (
    <div className={styles.listFilter}>
      {showTitle && <label className={styles.label}>{filter.title}</label>}
      <SelectButton
        size="middle"
        className={styles.select}
        placeholder={filter.placeHolder || "Select option"}
        options={options}
        value={currentValue}
        onChange={handleChange}
        onSelect={handleSelect}
        onSearchChange={handleSearch}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        searchValue={searchQuery || undefined}
        searchable
        loading={isFetching}
        allowClear
      />
    </div>
  );
}
